/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

import { PauseChip } from '../components/pause-chip';
import { Picture } from '../components/picture';
import { CONTAINER_CLASS, EYEBROW_CLASS, GOLD_EM_CLASS, SECTION_CLASS, SECTION_HEAD_CLASS, SECTION_LEAD_CLASS, SECTION_TITLE_CLASS, SectionBackground, SimChip } from '../components/ui';
import { V4A_NAMESPACE } from '../constants';
import { useReducedMotion } from '../hooks/use-page-motion';

/* Illustrative routes out of São Paulo: [route delay, flow duration, flow delay, ping delay, x, y] */
interface MapRoute {
  d: string;
  best?: boolean;
  delay: string;
  flow: string;
  flowDelay: string;
  pingDelay: string;
  x: number;
  y: number;
}

const ROUTES: readonly MapRoute[] = [
  { d: 'M372 505 Q 280 380 295 265', delay: '120ms', flow: '3.1s', flowDelay: '1.5s', pingDelay: '1.4s', x: 295, y: 265 },
  { d: 'M372 505 Q 500 300 560 250', best: true, delay: '0ms', flow: '2.2s', flowDelay: '1.3s', pingDelay: '1.9s', x: 560, y: 250 },
  { d: 'M372 505 Q 700 180 983 285', delay: '240ms', flow: '3.6s', flowDelay: '1.9s', pingDelay: '2.4s', x: 983, y: 285 },
  { d: 'M372 505 Q 720 645 985 545', delay: '360ms', flow: '3.4s', flowDelay: '2.4s', pingDelay: '2.9s', x: 985, y: 545 },
];

const varStyle = (values: Record<string, string>) => values as CSSProperties;

const STAT_CLASS = 'stat min-w-0 rounded-[var(--r-lg)] border border-solid border-[color:var(--rim-2)] p-[clamp(.85rem,_.6rem_+_.8vw,_1.15rem)] forced-colors:border-[color:CanvasText]';
const RAIL_FIGURE_CLASS = 'whitespace-nowrap text-[length:clamp(1.05rem,_.9rem_+_.55vw,_1.35rem)] font-bold text-[color:var(--fg-strong)]';
const RAIL_LABEL_CLASS = 'font-mono text-[length:var(--fs-2xs)] uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--fg-faint-aa)]';
const CNTBOX_CLASS = 'cntbox inline-block whitespace-nowrap text-start normal-case tracking-[inherit] text-inherit';

interface Stat {
  key: string;
  to: number;
  meter: string;
}

const STATS: readonly Stat[] = [
  { key: 'players', to: 3000000, meter: '92%' },
  { key: 'servers', to: 2000, meter: '78%' },
  { key: 'countries', to: 150, meter: '70%' },
  { key: 'games', to: 3000, meter: '84%' },
];

/** "O mapa que revela todas as rotas": the network map, its live routes and the counted figures. */
export function NetworkMap() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const reduced = useReducedMotion();
  const mapRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLElement>(null);
  const [paused, setPaused] = useState(false);

  // Routes redraw on each arrival; packets and node pings loop while on screen (classes on the static
  // `.map`, which the scroll reveal also writes to).
  useEffect(() => {
    const map = mapRef.current;
    const card = cardRef.current;
    if (!map || !card) return undefined;
    if (reduced || typeof IntersectionObserver !== 'function') {
      map.classList.add('is-drawn');
      if (!reduced) map.classList.add('is-live');
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) map.classList.add('is-drawn', 'is-live');
          else if (!entry.isIntersecting) map.classList.remove('is-drawn', 'is-live');
        });
      },
      { threshold: [0, 0.2, 0.5] }
    );
    observer.observe(card);
    return () => observer.disconnect();
  }, [reduced]);

  useEffect(() => {
    mapRef.current?.classList.toggle('is-paused', paused);
  }, [paused]);

  return (
    <section className={`${SECTION_CLASS} sec--map`} id='mapa' aria-labelledby='map-title'>
      <SectionBackground halos={[{ position: 'right' }]} />
      <div className={CONTAINER_CLASS}>
        <div className={`${SECTION_HEAD_CLASS} mb-[clamp(2rem,_1rem_+_3vw,_3.5rem)]`} data-reveal=''>
          <div className='min-w-0'>
            <p className={EYEBROW_CLASS}>{t('map.eyebrow')}</p>
            <h2 className={SECTION_TITLE_CLASS} id='map-title'>
              <Trans t={t} i18nKey='map.title' components={{ em: <em className={GOLD_EM_CLASS} /> }} />
            </h2>
          </div>
          <p className={`${SECTION_LEAD_CLASS} m-0`}>{t('map.lead')}</p>
        </div>

        <div ref={mapRef} className='map grid items-start gap-[clamp(1.5rem,_1rem_+_2.5vw,_3rem)] min-[960px]:grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)]' data-reveal=''>
          <figure ref={cardRef} className='map__card relative m-0 w-full min-w-0 justify-self-stretch overflow-hidden rounded-[var(--r-xl)] border border-solid border-[color:var(--rim)] p-[clamp(.75rem,_.5rem_+_1vw,_1.1rem)] [box-shadow:var(--shadow-lg)]'>
            <div className='map__plate relative mx-auto min-w-0 max-w-[560px]'>
              <Picture name='mapa' widths={[640, 1121]} sizes='(min-width: 64em) 60vw, 100vw' alt={t('map.image_alt')} width={1121} height={660} loading='lazy' className='h-auto w-full' />
              <svg className='map__routes' viewBox='0 0 1121 660' width='1121' height='660' preserveAspectRatio='none' aria-hidden='true' focusable='false'>
                <g fill='none' strokeLinecap='round'>
                  {ROUTES.map((route) => (
                    <path key={route.d} className={route.best ? 'map__route map__route--best' : 'map__route'} style={varStyle({ '--d': route.delay })} pathLength={1} d={route.d} />
                  ))}
                </g>
                <g fill='none' className='map__flows'>
                  {ROUTES.map((route) => (
                    <path key={route.d} className={route.best ? 'map__flow map__flow--best' : 'map__flow'} style={varStyle({ '--fd': route.flow, '--fdl': route.flowDelay })} pathLength={1} d={route.d} />
                  ))}
                </g>
                <g className='map__pings'>
                  {ROUTES.map((route) => (
                    <circle key={route.d} className='map__ping' style={varStyle({ '--pd': route.pingDelay })} cx={route.x} cy={route.y} r='7' />
                  ))}
                  <circle className='map__ping map__ping--home' style={varStyle({ '--pd': '1.2s' })} cx='372' cy='505' r='11' />
                </g>
                <g className='map__nodes'>
                  {ROUTES.map((route) => (
                    <circle key={route.d} className='map__node map__node--gold' cx={route.x} cy={route.y} r='7' />
                  ))}
                  <circle className='map__node map__node--home' cx='372' cy='505' r='11' />
                </g>
              </svg>
            </div>
            <figcaption className='map__cap mx-auto mb-0 mt-3 flex min-w-0 max-w-[560px] flex-wrap items-center gap-2 text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>
              <SimChip label={t('sim.label')} size='xs' /> {t('map.caption')}
              <PauseChip className='ms-auto' paused={paused} onToggle={() => setPaused((previous) => !previous)} labelPause={t('map.pause')} labelResume={t('map.resume')} hidden={reduced} />
            </figcaption>
          </figure>

          <div className='map__side grid min-w-0 gap-[clamp(1.25rem,_.75rem_+_1.5vw,_2rem)]'>
            <ul className='stats m-0 grid min-w-0 list-none grid-cols-2 gap-3 p-0' role='list' data-count-group=''>
              {STATS.map((stat) => (
                <li key={stat.key} className={STAT_CLASS}>
                  <p className='stat__v m-0 flex min-h-[clamp(2.25rem,_1.3rem_+_3vw,_3rem)] min-w-0 items-end gap-[.3rem]'>
                    <b className='stat__wide whitespace-nowrap text-[length:clamp(1.5rem,_1rem_+_2vw,_2.25rem)] font-bold leading-none tracking-[-.03em] [text-shadow:0_0_22px_rgb(var(--gold-rgb)_/_.3)]'>
                      <span className='cntbox inline-block whitespace-nowrap text-start' aria-hidden='true'>
                        <span className='cnt tabular-nums' data-count-to={stat.to}>
                          {t(`map.stats.${stat.key}.figure`)}
                        </span>
                        +
                      </span>
                      <span className='sr-only'>{t(`map.stats.${stat.key}.figure`)}+</span>
                    </b>
                  </p>
                  <p className='stat__k m-0 mt-[.35rem] min-w-0 text-[length:var(--fs-xs)] text-[color:var(--fg-dim)]'>{t(`map.stats.${stat.key}.label`)}</p>
                  <span className='stat__meter mt-[.7rem] block h-[4px] min-w-0 overflow-hidden rounded-[2px] bg-[color:rgb(255_255_255_/_.08)] forced-colors:border forced-colors:border-solid forced-colors:border-[color:CanvasText]'>
                    <i className='block h-full w-[var(--w,_60%)] rounded-[2px]' style={varStyle({ '--w': stat.meter })} />
                  </span>
                </li>
              ))}
            </ul>

            <ul className='statsrail m-0 grid min-w-0 list-none grid-cols-2 gap-x-4 gap-y-3 border-t border-solid border-t-[color:var(--rim)] p-0 min-[480px]:grid-cols-4' role='list' data-count-group=''>
              <li className='grid min-w-0 gap-[.15rem]'>
                <b className={RAIL_FIGURE_CLASS}>
                  {t('rail.up_to')}{' '}
                  <span className={CNTBOX_CLASS} aria-hidden='true'>
                    <span className='cnt normal-case tracking-[inherit] text-inherit' data-count-to='80'>
                      80
                    </span>
                    %
                  </span>
                  <span className='sr-only'>80%</span>
                </b>
                <span className={RAIL_LABEL_CLASS}>{t('rail.less_ping')}</span>
              </li>
              <li className='grid min-w-0 gap-[.15rem]'>
                <b className={RAIL_FIGURE_CLASS}>
                  {t('rail.up_to')}{' '}
                  <span className={CNTBOX_CLASS} aria-hidden='true'>
                    <span className='cnt normal-case tracking-[inherit] text-inherit' data-count-to='110'>
                      110
                    </span>
                    %
                  </span>
                  <span className='sr-only'>110%</span>
                </b>
                <span className={RAIL_LABEL_CLASS}>{t('rail.more_fps')}</span>
              </li>
              <li className='grid min-w-0 gap-[.15rem]'>
                <b className={RAIL_FIGURE_CLASS}>
                  <span className={CNTBOX_CLASS} aria-hidden='true'>
                    <Trans t={t} i18nKey='rail.routes' components={{ count: <span className='cnt normal-case tracking-[inherit] text-inherit' data-count-to='5' /> }} />
                  </span>
                  <span className='sr-only'>{t('rail.routes_sr')}</span>
                </b>
                <span className={RAIL_LABEL_CLASS}>{t('rail.simultaneous')}</span>
              </li>
              <li className='grid min-w-0 gap-[.15rem]'>
                <b className={RAIL_FIGURE_CLASS}>{t('rail.trial')}</b>
                <span className={RAIL_LABEL_CLASS}>{t('rail.no_card')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className='np-proof-note mx-auto mb-0 mt-[clamp(1.5rem,_1rem_+_1.6vw,_2.25rem)] w-[calc(min(100%,_var(--container-wide))_-_2_*_var(--gutter))] max-w-none rounded-[var(--r-lg)] border border-solid border-[color:var(--rim)] px-5 py-4 text-[length:var(--fs-sm)] leading-[1.55] text-[color:var(--fg-dim)]'>
        <Trans t={t} i18nKey='map.packet_note' components={{ strong: <strong className='font-semibold text-[color:var(--gold-hi)]' /> }} />
      </div>
    </section>
  );
}
